import { useEffect, useState } from "react"
import { Container } from "reactstrap"
import withRouter from "Components/Common/withRouter"
import Breadcrumb from "Components/organisms/breadcrumb"
import LoadingState from "Components/organisms/loading-state"
import DetailPageHeader from "Components/molecule/cards/detail-page-header"
import Tabs from "Components/molecule/tabs"
import ActionButton from "Components/atoms/buttons/action-button"
import { PencilIcon } from "Components/atoms/icons"
import BudgetFormModal from "Components/organisms/expense-budget/budget-form-modal"
import SummaryTab from "Components/organisms/expense-budget/summary-tab"
import LinesTab from "Components/organisms/expense-budget/lines-tab"
import MappingTab from "Components/organisms/expense-budget/mapping-tab"
import MonthlyTab from "Components/organisms/expense-budget/monthly-tab"
import CostCodesTab from "Components/organisms/expense-budget/cost-codes-tab"
import AlertsTab from "Components/organisms/expense-budget/alerts-tab"
import SettingsTab from "Components/organisms/expense-budget/settings-tab"
import {
    ExpenseBudget, ExpenseBudgetLine, ExpenseBudgetPayload, ExpenseBudgetSearchFields, employeeName,
} from "models/expense-budget"
import {
    getExpenseBudget, getExpenseBudgetLines, getExpenseBudgetSearchFields, updateExpenseBudget,
} from "slices/finance/expense-budget/thunk"
import { navigateToExpenseBudgets } from "utils/navigator"
import { formatCurrency } from "utils/tools"

const TAB_HEADERS = ["Summary", "Budget", "Cost Codes", "Mapping", "Monthly", "Alerts", "Settings"]

const ExpenseBudgetDetailsPage = (props: any) => {
    document.title = "Expense Budget | IbexIQ"
    const expenseBudgetId = new URLSearchParams(props.router.location.search).get("expenseBudgetId") ?? ""
    const [budget, setBudget] = useState<ExpenseBudget | null>(null)
    const [lines, setLines] = useState<ExpenseBudgetLine[]>([])
    const [searchFields, setSearchFields] = useState<ExpenseBudgetSearchFields | null>(null)
    const [activeTab, setActiveTab] = useState(TAB_HEADERS[0])
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const loadBudget = async () => setBudget(await getExpenseBudget(expenseBudgetId))
    const loadLines = async () => setLines((await getExpenseBudgetLines(expenseBudgetId)) ?? [])

    useEffect(() => {
        loadBudget()
        loadLines()
        getExpenseBudgetSearchFields().then(setSearchFields)
    }, [expenseBudgetId])

    const handleSaveHeader = async (payload: ExpenseBudgetPayload) => {
        setIsSaving(true)
        const updated = await updateExpenseBudget(expenseBudgetId, payload)
        setIsSaving(false)
        if (!updated) return
        setBudget(updated)
        setIsFormOpen(false)
    }

    const renderContent = (current: ExpenseBudget) => {
        const tabs = {
            Summary: <SummaryTab budget={current} />,
            Budget: <LinesTab budget={current} lines={lines} onBudgetChanged={loadBudget} onLinesChanged={loadLines} />,
            "Cost Codes": <CostCodesTab budget={current} onBudgetChanged={loadBudget} />,
            Mapping: <MappingTab budget={current} lines={lines} onLinesChanged={loadLines} />,
            Monthly: <MonthlyTab budget={current} lines={lines} />,
            Alerts: <AlertsTab budget={current} />,
            Settings: <SettingsTab budget={current} searchFields={searchFields} onBudgetChanged={loadBudget} onLinesChanged={loadLines} />,
        }
        return <>
            <DetailPageHeader
                title={`${current.department.code} — ${current.department.description} · FY${current.fiscalYearId}`}
                rows={[
                    { label: "Owner", value: employeeName(current.owner) },
                    { label: "Approved", value: formatCurrency(current.approvedAmount) },
                    { label: "Cost codes", value: current.costCodes.length },
                    { label: "Lines", value: lines.length },
                ]}
            />
            <div className="section-container">
                <Tabs headers={TAB_HEADERS} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
        </>
    }

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <Breadcrumb
                    title="Finance"
                    breadcrumbItem="Expense Budget"
                    actions={<>
                        <ActionButton onClick={() => navigateToExpenseBudgets(props.router.navigate)}>Back</ActionButton>
                        {budget && <>
                            <ActionButton onClick={() => setIsFormOpen(true)}>Edit <PencilIcon /></ActionButton>
                        </>}
                    </>}
                />
                {budget ? renderContent(budget) : (
                    <div className="section-container">
                        <LoadingState title="Loading Expense Budget" subtitle="Fetching budget details..." />
                    </div>
                )}
                <BudgetFormModal isOpen={isFormOpen} budget={budget} searchFields={searchFields} isSaving={isSaving}
                    onClose={() => setIsFormOpen(false)} onSave={handleSaveHeader} />
            </Container>
        </div>
    )
}

export default withRouter(ExpenseBudgetDetailsPage)
